
import { useState, useEffect, useRef } from "react";
import { Video, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useNotifications } from "@/hooks/useNotifications";
import { supabase } from "@/integrations/supabase/client";
import { VideoPlayer } from "@/components/ui/video-player";
import { isValidVideoUrl } from "@/utils/videoUtils";
import { compressVideo, isFFmpegSupported } from "@/utils/videoCompression";
import { Progress } from "@/components/ui/progress";

interface VideoSectionProps {
  exerciseId: string;
  videoUrl: string;
  onUpdate: (exerciseId: string, field: string, value: string) => void;
}

export const VideoSection = ({ exerciseId, videoUrl, onUpdate }: VideoSectionProps) => {
  const [showVideoInput, setShowVideoInput] = useState(false);
  const [localVideoUrl, setLocalVideoUrl] = useState(videoUrl);
  const [isUploading, setIsUploading] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState<string>("");
  const { success, error: showError } = useNotifications();

  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // Auto-save when a valid URL is entered (debounced)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!localVideoUrl || localVideoUrl === videoUrl) return;

    debounceRef.current = setTimeout(() => {
      if (isValidVideoUrl(localVideoUrl)) {
        onUpdate(exerciseId, "video_url", localVideoUrl);
        setShowVideoInput(false);
      }
    }, 800);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [localVideoUrl]);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('video/')) {
      showError("Por favor, sube solo archivos de video.");
      return;
    }

    if (file.size > 100 * 1024 * 1024) {
      showError("El archivo es demasiado grande. El tamaño máximo es 100MB.");
      return;
    }

    setIsUploading(true);
    setCompressionProgress(0);

    try {
      const originalSize = (file.size / 1024 / 1024).toFixed(2);
      let fileToUpload: File = file;
      let reduction = "0";

      if (isFFmpegSupported()) {
        setUploadStatus(`Comprimiendo video (${originalSize}MB)...`);

        fileToUpload = await compressVideo(file, (progress) => {
          setCompressionProgress(progress);
        });

        const compressedSize = (fileToUpload.size / 1024 / 1024).toFixed(2);
        reduction = ((1 - fileToUpload.size / file.size) * 100).toFixed(1);
        console.log(`Video comprimido: ${originalSize}MB → ${compressedSize}MB (${reduction}% reducción)`);
      } else {
        console.log("FFmpeg no soportado, subiendo video sin comprimir");
      }

      const uploadSize = (fileToUpload.size / 1024 / 1024).toFixed(2);
      setUploadStatus(`Subiendo video (${uploadSize}MB)...`);
      setCompressionProgress(0);

      const formData = new FormData();
      formData.append('file', fileToUpload);
      formData.append('exerciseId', exerciseId);

      const { data, error } = await supabase.functions.invoke('upload-exercise-video', {
        body: formData,
      });

      if (error) throw error;

      const publicUrl = data.url;

      setLocalVideoUrl(publicUrl);
      onUpdate(exerciseId, "video_url", publicUrl);
      setShowVideoInput(false);
      
      success("Video subido", {
        description: isFFmpegSupported() 
          ? `Video comprimido y subido. Reducción: ${reduction}%`
          : "Video subido exitosamente"
      });
    } catch (error) {
      console.error('Error uploading video:', error);
      showError(error instanceof Error ? error.message : "No se pudo subir el video. Por favor, intenta de nuevo.");
    } finally {
      setIsUploading(false);
      setUploadStatus("");
      setCompressionProgress(0);
    }
  };

  return (
    <div className="mt-4">
      {localVideoUrl && !showVideoInput ? (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-white/60">Video de referencia:</span>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowVideoInput(true)}
                className="text-clubbers-red hover:text-clubbers-red/80"
              >
                Editar
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setLocalVideoUrl("");
                  onUpdate(exerciseId, "video_url", "");
                }}
                className="text-clubbers-red hover:text-clubbers-red/80"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="aspect-video rounded-lg overflow-hidden">
            <VideoPlayer src={localVideoUrl} />
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Video className="h-4 w-4 text-primary sunset:text-[#f26018] forest:text-forest" />
            <span className="text-sm dark:text-white/60 sunset:text-white forest:text-white text-primary">
              {localVideoUrl ? "Editar video:" : "Agregar video:"}
            </span>
          </div>
          <Input
            placeholder="URL del video (YouTube, Vimeo, o video directo...)"
            value={localVideoUrl}
            onChange={(e) => setLocalVideoUrl(e.target.value)}
            className="bg-white/10 text-white border-white/20"
          />
          <div className="flex items-center gap-2 mt-2">
            <span className="text-sm dark:text-white/60 sunset:text-white forest:text-white text-primary">O sube un video:</span>
            <Input
              type="file"
              accept="video/*"
              onChange={handleFileUpload}
              className="hidden"
              id={`video-upload-${exerciseId}`}
            />
            <Button
              variant="outline"
              size="sm"
              className="border-white/20 dark:text-white forest:bg-forest forest:text-white text-primary ocean:hover:bg-background 
              ocean:hover:text-white/50 sunset:bg-[#f26018]"
              onClick={() => document.getElementById(`video-upload-${exerciseId}`)?.click()}
              disabled={isUploading}
            >
              <Upload className="h-4 w-4 mr-2" />
              {isUploading ? uploadStatus || "Subiendo..." : "Subir video"}
            </Button>
          </div>
          
          {isUploading && uploadStatus && (
            <div className="mt-3 space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-white/60">{uploadStatus}</span>
                {compressionProgress > 0 && (
                  <span className="text-white/60">{compressionProgress}%</span>
                )}
              </div>
              {compressionProgress > 0 && (
                <Progress value={compressionProgress} className="h-1" />
              )}
            </div>
          )}
          
          <div className="text-xs text-white/50 mt-2">
            💡 Los videos se comprimen automáticamente para ocupar menos espacio
          </div>
        </div>
      )}
    </div>
  );
};
